import moment from 'moment';
import React, { JSX } from 'react';

import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { timezonesSelectOptions } from '~/src/dash/globals/timezone';
import {
  Col,
  Cols,
  IndependentSelectPill,
  spacing,
  Text,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import {
  auctionInsightsTimeRanges,
  DateRange,
  IDateRange,
  ITimeRange,
  shortDateRanges,
  TimeRange,
} from '~/src/dash/utils/dateTimeRanges';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';

interface IProps {
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  currentDateRange: IDateRange | ITimeRange;
  isDisabled?: boolean;
  handleApplyDatesChange: ({ newStartDate, newEndDate }: INewCalDates) => void;
  loadState: LoadingState;
  labelWidth: number;
  labelHeight: number;
  selectedTimezone: string;
  handleApplyTimezone: (timezone: Set<string | number>) => void;
  isRealtimeOnly: boolean;
  handleToggleRealtimeOnly: () => void;
  selectedTimeRange: TimeRange;
  handleApplyTimeRange: (timeRange: TimeRange) => void;
}

export const AuctionInsightsPeriods = ({
  calStartDate,
  calEndDate,
  currentDateRange,
  handleApplyDatesChange,
  isDisabled,
  isRealtimeOnly,
  handleToggleRealtimeOnly,
  loadState,
  labelWidth,
  labelHeight,
  selectedTimezone,
  handleApplyTimezone,
  selectedTimeRange,
  handleApplyTimeRange,
}: IProps): JSX.Element => {
  const { t } = usePublicaTranslation();
  const handleToggleDateRange = (rangeKey: DateRange): void => {
    // Find the selected range configuration
    const selectedRange = shortDateRanges.find((range) => range.key === rangeKey);
    if (selectedRange) {
      // Generate the query dates for this range
      const { startDate, endDate } = selectedRange.generateQueryDates(selectedTimezone, false);
      // Pass the new dates to the parent handler
      handleApplyDatesChange({
        newStartDate: startDate,
        newEndDate: endDate,
      });
    }
  };

  const handleCalendarChange = ({ newStartDate, newEndDate }: INewCalDates): void => {
    // Pass the dates to the parent handler
    // The DateTimeRangeToggles activeKey will automatically update based on the new props
    handleApplyDatesChange({
      newStartDate,
      newEndDate,
    });
  };

  return (
    <Cols gap={spacing.base} style={{ alignItems: 'center', minHeight: `${labelHeight}px` }}>
      {/* LABEL */}
      <Col style={{ minWidth: `${labelWidth}px` }}>
        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
          <Col displayFlex>
            <IconV2 name='calendar' small />
          </Col>
          <Col>
            <Text small bold tKey='Period' singleLineLabel />
          </Col>
        </Cols>
      </Col>
      <Col>
        <Cols
          wrap
          gap={spacing.base}
          style={{
            alignItems: 'center',
            minHeight: `${labelHeight}px`,
            rowGap: spacing.extraSmall,
          }}
        >
          <Col>
            <PillSwitcherV2
              isDisabled={loadState === LoadingStateLoading}
              size='smallExtraLong'
              options={[
                {
                  value: 'Custom Date',
                  tKey: 'CustomDate',
                  label: 'Custom Date',
                  isSelected: !isRealtimeOnly,
                  key: 'customDate',
                },
                {
                  value: 'Real-Time (L24 hrs)',
                  label: 'Real-Time (L24 hrs)',
                  tKey: 'RealTimeToggleText',
                  isSelected: isRealtimeOnly,
                  key: 'realTime',
                },
              ]}
              onChange={(value) => {
                // Only toggle if user clicked on a different option
                const shouldBeRealtime = value === 'Real-Time (L24 hrs)';
                if (shouldBeRealtime !== isRealtimeOnly) {
                  handleToggleRealtimeOnly();
                }
              }}
              tooltipText={
                isRealtimeOnly ? t('RealTimeToggleTooltipText') : t('CustomDateTooltipText')
              }
            />
          </Col>
          {/* Calendar */}
          {!isRealtimeOnly && (
            <>
              <Col style={{ whiteSpace: 'nowrap' }}>
                <CalendarV2
                  calStartDate={calStartDate}
                  calEndDate={calEndDate}
                  handleApplyDatesChange={handleCalendarChange}
                  isDisabled={isDisabled || loadState === LoadingStateLoading}
                  uniqueID='insightsCal'
                  minDate={moment('2024-03-01').startOf('day')} // no data before this date
                  maxDate={moment().endOf('day')}
                  customInitialVisibleMonth={() => calEndDate.clone().subtract(1, 'month')}
                />
              </Col>
              <Col>
                <TooltipWithTranslation tKey='TooltipAuctionInsightsMinDate'>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <IconV2 name='info-static' color={publicaColors.veryDarkGray} />
                  </div>
                </TooltipWithTranslation>
              </Col>
            </>
          )}
          <Col>
            {!isRealtimeOnly ? (
              <DateTimeRangeToggles
                ranges={shortDateRanges}
                activeKey={currentDateRange.key as DateRange}
                onClick={handleToggleDateRange}
              />
            ) : (
              <DateTimeRangeToggles
                ranges={auctionInsightsTimeRanges}
                activeKey={selectedTimeRange}
                onClick={handleApplyTimeRange}
              />
            )}
          </Col>
          <Col>
            <IndependentSelectPill
              disableSearch
              displayLabelWithSelection
              displaySelectedOption
              enableLabelTooltip
              extractStringToSort={(option: ISelectOption) => option.label}
              handleApplySelect={handleApplyTimezone}
              isCheckboxHidden={true}
              isSelectionRequired={true}
              key={'Timezone'}
              labelTKey={'Timezone'}
              options={timezonesSelectOptions}
              selectKey={'Timezone'}
              selectedLimit={1}
              selectedOptionIDs={new Set([selectedTimezone])}
            />
          </Col>
        </Cols>
      </Col>
    </Cols>
  );
};
